<script>
import { DateTime } from 'luxon'
import mixinComponent from '../../../Mixins/mixinComponent'
import FormCreator from '../Form/FormCreator.vue'
import FieldCreator from '../Form/FieldCreator.vue'
import TableDateRangeFilter from './TableDateRangeFilter.vue'

export default {
  components: { TableDateRangeFilter, FieldCreator, FormCreator },
  mixins: [mixinComponent],
  props: {
    filters: {
      type: Array,
      default: [],
    },
    endpoint: {
      type: String,
      default: '',
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    filterValues: {
      type: Object,
      default: () => ({}),
    },
    showCalendarFilter: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update-data', 'set-date'],
  data() {
    return {
      loadField: false,
      availableFiltersKeys: [],
    }
  },
  watch: {
    filterOptions: {
      handler(newOptions) {
        this.applyFilterOptions(newOptions)
      },
      deep: true,
    },
  },
  methods: {
    updateFormData(newData) {
      if (!Object.keys(newData.datas).length) {
        return
      }
      this.$emit('update-data', newData.datas)
    },
    updateData(value, choices, text, name) {
      this.updateFormData({ datas: { ...this.$refs.filter.datas, ...{ [name]: value } } })
    },
    updateInputData(value, event, name) {
      if (this.isDateField(name)) {
        this.updateDateData(value, name)
        return
      }
      this.updateFormData({ datas: { ...this.$refs.filter.datas, ...{ [name]: value } } })
    },
    updateDateData(date, name) {
      const value = this.toIsoDate(date)
      this.$refs.filter.datas[name] = value
      this.updateFormData({ datas: { ...this.$refs.filter.datas, [name]: value } })
    },
    isDateField(name) {
      return this.$refs.filter?.def?.[name]?.type === 'date'
    },
    toIsoDate(value) {
      if (!value) {
        return null
      }
      if (DateTime.isDateTime(value)) {
        return value.isValid ? value.toISODate() : null
      }
      let date = DateTime.fromISO(String(value))
      if (!date.isValid) {
        date = DateTime.fromFormat(String(value), 'dd/MM/yyyy')
      }
      return date.isValid ? date.toISODate() : null
    },
    setDateDataTable(fromValue, toValue) {
      this.$emit('set-date', fromValue, toValue)
    },
    setBuild() {
      this.loadField = true
      if (Object.keys(this.filterOptions).length > 0) {
        this.applyFilterOptions(this.filterOptions)
      }
    },
    applyFilterOptions(options) {
      const formCreator = this.$refs.filter
      if (!formCreator?.def) {
        return
      }

      const preservedDatas = { ...formCreator.datas }

      let newAvailableFiltersKeys = []
      for (const [fieldName, field] of Object.entries(formCreator.def)) {
        if (!['choice', 'select'].includes(field.type)) {
          newAvailableFiltersKeys = [...newAvailableFiltersKeys, fieldName]
          continue
        }
        const filter = this.filters.find((f) => f.name === fieldName)
        const fieldOptionsKey = filter?.optionsKey || fieldName

        const choices = options?.[fieldOptionsKey] || field.options?.choices || []
        if (!choices.length) {
          continue
        }

        newAvailableFiltersKeys = [...newAvailableFiltersKeys, fieldName]
        formCreator.def[fieldName].options.choices = Object.fromEntries(
          choices.map((choice) => [choice.name, choice.id]),
        )
      }

      this.availableFiltersKeys = newAvailableFiltersKeys

      Object.assign(formCreator.datas, preservedDatas)
    },
  },
}
</script>
<template>
  <div class="row col-md-12 justify-content-center">
    <FormCreator
      @update-data="updateFormData"
      ref="filter"
      :endpoint="endpoint"
      specific-endpoint="filter"
      class-to-add="d-none"
      @form-build="setBuild"
    />
    <template v-if="loadField">
      <div
        class="col-md-3"
        :class="{ 'd-none': !availableFiltersKeys.includes(filter.name) }"
        v-for="(filter, index) in filters"
        :key="index"
      >
        <FieldCreator
          :form-data="$refs.filter"
          :name="filter.name"
          form-ref="filter"
          :domain="filter.domain"
          :label="filter.label ?? null"
          :label-top="filter.labelTop"
          :type-value="filter.typeValue ?? 'int'"
          :ignore-user-readonly="true"
          :min-datetime="filter.minDatetime ?? null"
          :max-datetime="filter.maxDatetime ?? null"
          @get-select-change="updateData"
          @get-input-change="updateInputData"
          @get-date-change="(date) => updateDateData(date, filter.name)"
        />
      </div>
    </template>
    <TableDateRangeFilter v-if="showCalendarFilter" @set-date="setDateDataTable" />
  </div>
</template>
